import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Button,
  Chip,
  styled,
  alpha,
  useTheme,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EditNoteIcon from "@mui/icons-material/EditNote";
import PublishIcon from "@mui/icons-material/Publish";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import NotificationSnackbar from "./NotificationBar";
const ModernTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 500,
  padding: theme.spacing(1.8, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  whiteSpace: "nowrap",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: alpha(theme.palette.primary.main, 0.015),
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.045),
    transition: "background-color 0.2s ease",
  },
}));

const ResponsiveTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "650px",
  overflowX: "auto",
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "12px",
  "&::-webkit-scrollbar": { height: "8px", width: "8px" },
  "&::-webkit-scrollbar-track": {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(theme.palette.primary.main, 0.25),
    borderRadius: "4px",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.4) },
  },
}));

function TempPayrolls() {
  const theme = useTheme();
  const PRIMARY_COLOR = "#0a2e52";
  const SECONDARY_COLOR = "#1565C0";

  const [searchTerm, setSearchTerm] = useState("");
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [payrollForStatusChange, setPayrollForStatusChange] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [payrollForForce, setPayrollForForce] = useState(null);
  const [forceUpdating, setForceUpdating] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const showSnackbar = (msg, severity = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  const handleMenuOpen = (event, payroll) => {
    setAnchorEl(event.currentTarget);
    setSelectedPayroll(payroll);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPayroll(null);
  };
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      showSnackbar("Please enter a search term.", "warning");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setPayrolls([]);
    try {
      const response = await fetch(
        "/api/support/temp-payrolls-by-search-term",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ searchTerm: searchTerm.trim() }),
        },
      );
      const data = await response.json();
      if (response.ok) {
        const payrollsArray = data ? Object.values(data) : [];
        setPayrolls(payrollsArray);
        if (payrollsArray.length === 0) {
          showSnackbar("No temporary payrolls found.", "info");
        } else {
          showSnackbar(`Found ${payrollsArray.length} payroll(s).`, "success");
        }
      } else {
        const errorMsg = data.message || "Failed to fetch payrolls.";
        showSnackbar(errorMsg, "error");
      }
    } catch (error) {
      console.error("Error fetching temp payrolls:", error);
      showSnackbar("An error occurred while fetching payrolls.", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "0 RWF";
    return parseInt(amount, 10).toLocaleString() + " RWF";
  };
  const handleChangeStatus = () => {
    if (selectedPayroll) {
      setPayrollForStatusChange(selectedPayroll);
      setStatusDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleCloseStatusDialog = () => {
    if (statusUpdating) return;
    setStatusDialogOpen(false);
    setPayrollForStatusChange(null);
  };

  const handleConfirmChangeStatus = async () => {
    if (!payrollForStatusChange) return;

    const { payrollId } = payrollForStatusChange;
    setStatusUpdating(true);

    try {
      const response = await fetch("/api/support/change-payroll-status", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payrollId }),
      });
      const data = await response.json();
      if (response.ok) {
        setPayrolls((prev) =>
          prev.map((p) =>
            p.payrollId === payrollId
              ? { ...p, ...(data.payroll || { payrollStatus: "Generated" }) }
              : p,
          ),
        );
        showSnackbar(
          data.message || "Payroll status updated successfully.",
          "success",
        );
        setStatusDialogOpen(false);
        setPayrollForStatusChange(null);
      } else {
        showSnackbar(
          data.message || "Failed to update payroll status.",
          "error",
        );
      }
    } catch (error) {
      console.error("Error changing payroll status:", error);
      showSnackbar("An error occurred while updating payroll status.", "error");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleForceDeclarations = () => {
    if (selectedPayroll) {
      setPayrollForForce(selectedPayroll);
      setForceDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleCloseForceDialog = () => {
    if (forceUpdating) return;
    setForceDialogOpen(false);
    setPayrollForForce(null);
  };

  const handleConfirmForceDeclarations = async () => {
    if (!payrollForForce) return;

    const { payrollId } = payrollForForce;
    setForceUpdating(true);

    try {
      const response = await fetch("/api/support/force-payroll-declarations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payrollId }),
      });
      const data = await response.json();
      if (response.ok) {
        showSnackbar(
          data.message || "Payroll declarations forced successfully.",
          "success",
        );
        setForceDialogOpen(false);
        setPayrollForForce(null);
      } else {
        showSnackbar(data.message || "Failed to force declarations.", "error");
      }
    } catch (error) {
      console.error("Error forcing declarations:", error);
      showSnackbar("An error occurred while forcing declarations.", "error");
    } finally {
      setForceUpdating(false);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "16px",
        p: { xs: 2, sm: 4 },
        mb: 3,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
        minHeight: "75vh",
      }}
    >
      {/* HEADER & SEARCH BAR */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          mb: 4,
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: PRIMARY_COLOR }}
          >
            Temporary Payrolls
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Search and manage temporary payroll entries by Costcenter, ID, or
            name.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search (e.g. MIFOTRA)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSearch();
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 300 } }}
          />
          <Button
            variant="contained"
            disableElevation
            onClick={handleSearch}
            disabled={loading}
            sx={{
              minHeight: 40,
              px: 3,
              backgroundColor: PRIMARY_COLOR,
              "&:hover": { backgroundColor: SECONDARY_COLOR },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Search"
            )}
          </Button>
        </Box>
      </Box>

      {/* TABLE CONTENT */}
      {loading ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <CircularProgress size={32} sx={{ color: PRIMARY_COLOR, mb: 2 }} />
          <Typography color="text.secondary">
            Fetching Temporary Payrolls...
          </Typography>
        </Box>
      ) : hasSearched && payrolls.length === 0 ? (
        <Box
          textAlign="center"
          py={10}
          px={2}
          sx={{
            backgroundColor: alpha(PRIMARY_COLOR, 0.02),
            borderRadius: 3,
            border: `1px dashed ${alpha(PRIMARY_COLOR, 0.1)}`,
          }}
        >
          <ReceiptLongIcon
            sx={{ fontSize: 60, color: alpha(PRIMARY_COLOR, 0.3), mb: 2 }}
          />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: PRIMARY_COLOR, mb: 1 }}
          >
            No Payrolls Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We couldn't find any temporary payrolls matching "{searchTerm}".
          </Typography>
        </Box>
      ) : payrolls.length > 0 ? (
        <>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
            Showing <strong>{payrolls.length}</strong> payrolls for "
            <strong>{searchTerm}</strong>"
          </Typography>

          <ResponsiveTableContainer component={Paper} elevation={0}>
            <Table size="small" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor:
                      theme.palette.primary.light || alpha(PRIMARY_COLOR, 0.8),
                    "& th": {
                      color: "white",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      borderBottom: "none",
                      py: 1.8,
                    },
                  }}
                >
                  <ModernTableCell>Payroll ID</ModernTableCell>
                  <ModernTableCell>Period</ModernTableCell>
                  <ModernTableCell>Costcenter</ModernTableCell>
                  <ModernTableCell>Employee Group</ModernTableCell>
                  <ModernTableCell>Source of Fund</ModernTableCell>
                  <ModernTableCell>Employees</ModernTableCell>
                  <ModernTableCell>Total Salary</ModernTableCell>
                  <ModernTableCell>Status</ModernTableCell>
                  <ModernTableCell align="center">Actions</ModernTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {payrolls.map((payroll) => (
                  <StyledTableRow key={payroll.payrollId} hover>
                    <ModernTableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                      >
                        {payroll.payrollId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        By: {payroll.generatedBy || "Unknown"}
                      </Typography>
                    </ModernTableCell>

                    <ModernTableCell>{payroll.period}</ModernTableCell>

                    <ModernTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {payroll.costcenter}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {payroll.sector}
                      </Typography>
                    </ModernTableCell>

                    <ModernTableCell>
                      <Typography variant="body2">
                        {payroll.employeeGroup}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {payroll.payrollGroup}
                      </Typography>
                    </ModernTableCell>

                    <ModernTableCell>{payroll.sourceOfFund}</ModernTableCell>

                    <ModernTableCell>
                      <Chip
                        label={payroll.numberOfEmployees}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: alpha(PRIMARY_COLOR, 0.08),
                          color: PRIMARY_COLOR,
                        }}
                      />
                    </ModernTableCell>

                    <ModernTableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#1e88e5" }}
                      >
                        {formatCurrency(payroll.totalSalary)}
                      </Typography>
                    </ModernTableCell>

                    <ModernTableCell>
                      <Chip
                        label={payroll.payrollStatus}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          backgroundColor:
                            payroll.payrollStatus === "Generated"
                              ? alpha(theme.palette.success.main, 0.15)
                              : alpha(theme.palette.warning.main, 0.15),
                          color:
                            payroll.payrollStatus === "Generated"
                              ? theme.palette.success.dark
                              : theme.palette.warning.dark,
                        }}
                      />
                    </ModernTableCell>

                    <ModernTableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, payroll)}
                        sx={{ color: "text.secondary" }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </ModernTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </ResponsiveTableContainer>

          {/* Action Menu attached to the 3 dots */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              elevation: 3,
              sx: {
                borderRadius: 2,
                minWidth: 180,
                mt: 1,
              },
            }}
          >
            <MenuItem onClick={handleChangeStatus}>
              <ListItemIcon>
                <EditNoteIcon
                  fontSize="small"
                  sx={{ color: SECONDARY_COLOR }}
                />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 500 }}
              >
                Change Status
              </ListItemText>
            </MenuItem>
            {/* <MenuItem onClick={handleForceDeclarations}>
              <ListItemIcon>
                <PublishIcon
                  fontSize="small"
                  sx={{ color: theme.palette.warning.dark }}
                />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ fontSize: "0.9rem", fontWeight: 500 }}
              >
                Force Declarations
              </ListItemText>
            </MenuItem> */}
          </Menu>
        </>
      ) : (
        <Box
          textAlign="center"
          py={10}
          px={2}
          sx={{
            backgroundColor: alpha(PRIMARY_COLOR, 0.02),
            borderRadius: 3,
            border: `1px dashed ${alpha(PRIMARY_COLOR, 0.1)}`,
          }}
        >
          <ReceiptLongIcon
            sx={{ fontSize: 60, color: alpha(PRIMARY_COLOR, 0.3), mb: 2 }}
          />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: PRIMARY_COLOR, mb: 1 }}
          >
            Ready to Search
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter a search term above (e.g., Costcenter name or ID) to fetch
            Temporary Payrolls.
          </Typography>
        </Box>
      )}

      {/* CHANGE STATUS CONFIRMATION MODAL */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleCloseStatusDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        sx={{ backgroundColor: "rgba(0, 0, 0, 0.7)" }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 1.8,
            px: 3,
            bgcolor: theme.palette.primary.light || alpha(PRIMARY_COLOR, 0.8),
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: alpha("#fff", 0.15),
              flexShrink: 0,
            }}
          >
            <WarningAmberIcon sx={{ color: "white" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "white" }}>
            Change Payroll Status
          </Typography>
          <IconButton
            onClick={handleCloseStatusDialog}
            disabled={statusUpdating}
            sx={{ ml: "auto", color: "white" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to change the status of this payroll to{" "}
            <strong>Generated</strong>? This action cannot be undone.
          </DialogContentText>

          {payrollForStatusChange && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(PRIMARY_COLOR, 0.04),
                border: `1px solid ${alpha(PRIMARY_COLOR, 0.1)}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Payroll ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: PRIMARY_COLOR }}
                >
                  {payrollForStatusChange.payrollId}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Costcenter
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {payrollForStatusChange.costcenter}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Current Status
                </Typography>
                <Chip
                  label={payrollForStatusChange.payrollStatus}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    backgroundColor:
                      payrollForStatusChange.payrollStatus === "Generated"
                        ? alpha(theme.palette.success.main, 0.15)
                        : alpha(theme.palette.warning.main, 0.15),
                    color:
                      payrollForStatusChange.payrollStatus === "Generated"
                        ? theme.palette.success.dark
                        : theme.palette.warning.dark,
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseStatusDialog}
            disabled={statusUpdating}
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleConfirmChangeStatus}
            disabled={statusUpdating}
            sx={{
              backgroundColor: PRIMARY_COLOR,
              "&:hover": { backgroundColor: SECONDARY_COLOR },
              minWidth: 110,
            }}
          >
            {statusUpdating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Confirm"
            )}
          </Button>
        </DialogActions>
          <NotificationSnackbar
            open={snackbarOpen}
            onClose={handleSnackbarClose}
            message={snackbarMessage}
            severity={snackbarSeverity}
          />

      </Dialog>

      {/* FORCE DECLARATIONS CONFIRMATION MODAL */}
      <Dialog
        open={forceDialogOpen}
        onClose={handleCloseForceDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        sx={{ backgroundColor: "rgba(0, 0, 0, 0.7)" }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            py: 1.8,
            px: 3,
            bgcolor: theme.palette.primary.light || alpha(PRIMARY_COLOR, 0.8),
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: alpha("#fff", 0.15),
              flexShrink: 0,
            }}
          >
            <PublishIcon sx={{ color: "white" }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "white" }}>
            Force Declarations
          </Typography>
          <IconButton
            onClick={handleCloseForceDialog}
            disabled={forceUpdating}
            sx={{ ml: "auto", color: "white" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, mt: 1 }}>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to force declarations for this payroll? This
            process will push the payroll declarations.
          </DialogContentText>

          {payrollForForce && (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Payroll ID
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: theme.palette.warning.dark }}
                >
                  {payrollForForce.payrollId}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.75,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Costcenter
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {payrollForForce.costcenter}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Employees
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {payrollForForce.numberOfEmployees}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleCloseForceDialog}
            disabled={forceUpdating}
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={handleConfirmForceDeclarations}
            disabled={forceUpdating}
            sx={{
              backgroundColor: PRIMARY_COLOR,
              "&:hover": { backgroundColor: SECONDARY_COLOR },
              minWidth: 110,
            }}
          >
            {forceUpdating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Force Declare"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <NotificationSnackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Box>
  );
}

export default TempPayrolls;
